// Parses the doc assistant's structured response into either an in-place edit
// (full revised HTML) or a conversational reply. Defensive: any malformed/empty
// result falls back to a reply showing the raw text, so the doc is never blanked.

export type DocEditCommand =
  | { action: 'edit'; html: string }
  | { action: 'reply'; text: string };

/**
 * Extracts the first brace-balanced `{...}` object from `s`, respecting string
 * literals and escapes. Returns the object substring, or null if none is
 * complete. Unlike `indexOf('{')` + `lastIndexOf('}')`, trailing prose that
 * contains a stray brace (an emoji ":}", a `${}`, a code sample) can't corrupt
 * the captured span and turn a valid edit into a fallback reply.
 */
function extractFirstJsonObject(s: string): string | null {
  const start = s.indexOf('{');
  if (start === -1) return null;

  let depth = 0;
  let inString = false;
  let escaped = false;

  for (let i = start; i < s.length; i++) {
    const ch = s[i];
    if (inString) {
      if (escaped) escaped = false;
      else if (ch === '\\') escaped = true;
      else if (ch === '"') inString = false;
      continue;
    }
    if (ch === '"') {
      inString = true;
    } else if (ch === '{') {
      depth++;
    } else if (ch === '}') {
      depth--;
      if (depth === 0) return s.slice(start, i + 1);
    }
  }
  return null; // never balanced — treat as no object
}

export function parseDocEditResponse(raw: string): DocEditCommand {
  const fallback: DocEditCommand = { action: 'reply', text: raw };

  // Strip ONLY a leading ```/```json fence and a trailing ``` fence. A global
  // strip would also remove backticks from inside the html/text payload (e.g.
  // a fenced code block the model legitimately added to the doc), corrupting
  // real content.
  const unfenced = raw
    .trim()
    .replace(/^```(?:json)?[ \t]*\r?\n?/i, '')
    .replace(/\r?\n?```\s*$/i, '')
    .trim();

  const candidate = extractFirstJsonObject(unfenced);
  if (candidate === null) return fallback;

  let parsed: any;
  try {
    parsed = JSON.parse(candidate);
  } catch {
    return fallback;
  }

  if (parsed && parsed.action === 'edit' && typeof parsed.html === 'string' && parsed.html.trim()) {
    return { action: 'edit', html: parsed.html };
  }
  // Require non-empty text so an empty reply doesn't render a blank bubble —
  // fall back to showing the raw response instead (some signal beats none).
  if (parsed && parsed.action === 'reply' && typeof parsed.text === 'string' && parsed.text.trim()) {
    return { action: 'reply', text: parsed.text };
  }
  return fallback;
}
